import {
	type OPERATOR_LIKE,
	type OPERATOR_EQUALS,
	type OPERATOR_IN,
	type Clause,
	type Jast,
	COMPOUND_OPERATOR_AND,
	creators,
	print,
	CLAUSE_TYPE_TERMINAL,
	OPERAND_TYPE_FUNCTION,
	OPERAND_TYPE_VALUE,
} from '@atlaskit/jql-ast';
import { AQL_FUNCTION_NAME, IQL_FUNCTION_NAME } from '../../../../common/constants.tsx';
import { escapeBackslashes, wrapWithQuotes } from '../../../../common/utils.tsx';

const createTerminalClause = (
	fieldId: string,
	value: string,
	operator: typeof OPERATOR_LIKE | typeof OPERATOR_EQUALS,
) =>
	// TODO: escapeBackslashes serves as a temporary patch to convert jqlTerm into a format suitable for creator API.
	// EM-6563 should resolve this by updating creator API to accept the semantic JQL value for jqlTerm currently returned by hydrateJqlQuery.
	creators.terminalClause(
		creators.field(escapeBackslashes(fieldId)),
		creators.operator(operator),
		creators.byText.valueOperand(wrapWithQuotes(escapeBackslashes(value))),
	);

export const getUpdatedJql = (
	ast: Jast,
	clause: Clause | undefined,
	fieldId: string,
	value: string,
	operator: typeof OPERATOR_LIKE | typeof OPERATOR_EQUALS,
): string => {
	if (!clause) {
		if (!value || !ast.query) {
			return ast.represents;
		}
		ast.query.appendClause(createTerminalClause(fieldId, value, operator), COMPOUND_OPERATOR_AND);
	} else if (!value) {
		clause.remove();
	} else {
		if (
			clause.clauseType === CLAUSE_TYPE_TERMINAL &&
			clause.operand?.operandType === OPERAND_TYPE_VALUE &&
			clause.operand.value === value &&
			clause.operator?.value === operator
		) {
			// If the clause already exists and the value is the same, return the original JQL
			// without modifying the AST. This is to prevent the introduction of stale clause objects.
			return ast.represents;
		}
		clause.replace(createTerminalClause(fieldId, value, operator));
	}

	return print(ast, {
		operatorCase: 'upper',
	});
};

const createAqlTerminalClause = (fieldId: string, value: string, operator: typeof OPERATOR_IN) =>
	// TODO: escapeBackslashes serves as a temporary patch to convert jqlTerm into a format suitable for creator API.
	// EM-6563 should resolve this by updating creator API to accept the semantic JQL value for jqlTerm currently returned by hydrateJqlQuery.
	creators.terminalClause(
		creators.field(escapeBackslashes(fieldId)),
		creators.operator(operator),
		// Confirmed with the team that they prefer to use aqlFunction instead of iqlFunction
		creators.byText.valueOperand(
			`${AQL_FUNCTION_NAME}(${wrapWithQuotes(escapeBackslashes(value))})`,
		),
	);

export const getUpdatedJqlWithAql = (
	ast: Jast,
	clause: Clause | undefined,
	fieldId: string,
	value: string,
	operator: typeof OPERATOR_IN,
): string => {
	if (!clause) {
		if (!value || !ast.query) {
			return ast.represents;
		}
		ast.query.appendClause(
			createAqlTerminalClause(fieldId, value, operator),
			COMPOUND_OPERATOR_AND,
		);
	} else if (!value) {
		clause.remove();
	} else {
		if (getAqlFromClause(clause) === value && clause.operator?.value === operator) {
			// If the clause already exists and the value is the same, return the original JQL
			// without modifying the AST. This is to prevent the introduction of stale clause objects.
			return ast.represents;
		}
		clause.replace(createAqlTerminalClause(fieldId, value, operator));
	}

	return print(ast, {
		operatorCase: 'upper',
	});
};

export const getAqlFromClause = (clause: Clause | undefined): string =>
	(clause &&
		clause.clauseType === CLAUSE_TYPE_TERMINAL &&
		clause.operand &&
		clause.operand.operandType === OPERAND_TYPE_FUNCTION &&
		(clause.operand.function.value.toLowerCase() === AQL_FUNCTION_NAME.toLowerCase() ||
			clause.operand.function.value.toLowerCase() === IQL_FUNCTION_NAME.toLowerCase()) &&
		clause.operand.arguments[0].value) ||
	'';
